import { useCallback, useEffect, useState } from 'react'
import { WithRenderedCustomFields } from '../../types'
import { CIMSSingleColumn } from '../../../CIMSTable/CIMSColumn/types'
import { useProjectCustomFieldsContext } from '../../ProjectCustomFieldsContext'
import {
  CustomFieldDataType,
  CustomFieldEntity,
  CustomListItemStyle,
  Report,
  alphabeticalSort,
} from '@cims-online/shared'
import EntityCustomFields from '../../components/EntityCustomFields'
import { CIMSTag } from '../../../CIMSTag'
import { CIMSTags } from '../../../CIMSTags'
import ReportCustomFields from '../../components/ReportCustomFields'
import CIMSCheckbox from '../../../CIMSCheckbox'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'
import { align } from './utils/align'
import { find } from './utils/find'
import { UseProminentCustomFieldsOptions } from './types'
import { sortOptions } from './utils/sort'
import { width } from './utils/width'
import CIMSChecklist from '../../../CIMSChecklist'
import { ProjectCustomField } from '../../ProjectCustomFieldsContext/types'
import { dateFormatter } from '../../../../utils/dateFormatter'

/**
 * a reusable hook to generate {@link CIMSSingleColumn} objects for use in a {@link CIMSTable}
 *
 * you should use the {@link ColumnOptions} version of the {@link CIMSTable.columns} prop
 * instead of using this manually
 */
export function useProminentCustomFields<T extends WithRenderedCustomFields>({
  onCheckBoolean,
  records,
  onCheckBooleans,
  disableEditWhen,
}: UseProminentCustomFieldsOptions<T>) {
  const {
    customFields,
    classType,
    disabledById,
    renderedAsCheckboxes,
    customFieldIdToProjectCustomField,
  } = useProjectCustomFieldsContext()
  const [columns, setColumns] = useState<CIMSSingleColumn<T>[]>([])
  // booleans
  const [singleLoading, setSingle] = useKeyedBooleans()
  const single = useCallback<
    UseProminentCustomFieldsOptions<T>['onCheckBoolean']
  >(
    async (record, { customFieldId, value }) => {
      const key = `${customFieldId}-${JSON.stringify(record)}`
      setSingle.add(key)
      await onCheckBoolean?.(record, { customFieldId, value: !value })
      setSingle.remove(key)
    },
    // do not listen to setSingle => infinite re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCheckBoolean]
  )
  const filterOptions = useCallback<
    (customField: ProjectCustomField) => CIMSSingleColumn<T>['filter']
  >(
    ({ id, metadata, dataTypeId }) => {
      if (dataTypeId === CustomFieldDataType.Text.id)
        return {
          type: 'search',
          with: 'callback',
          onFilter: (value, { customFields }) =>
            find(customFields?.textValues, id)
              ?.value?.toLowerCase()
              .includes(value?.toString().toLowerCase()) ?? false,
        }
      else if (dataTypeId === CustomFieldDataType.Number.id)
        return {
          type: 'number',
          with: 'value',
          value: ({ customFields }) =>
            find(customFields?.numberValues, id)?.value,
        }
      else if (dataTypeId === CustomFieldDataType.Entity.id)
        return {
          with: 'callback',
          items: CustomFieldEntity.values.map(({ id, type }) => ({
            text: type,
            value: id,
          })),
          onFilter: (value, { customFields }) =>
            find(customFields?.entityValues, id)?.entities.some(
              ({ entityId }) => entityId === value
            ) ?? false,
        }
      else if (dataTypeId === CustomFieldDataType.Report.id)
        return {
          with: 'callback',
          items: Report.values.map(({ name, id }) => ({
            text: name,
            value: id,
          })),
          onFilter: (value, { customFields }) =>
            find(customFields?.reportValues, id)?.reports.some(
              ({ reportId }) => reportId === value
            ) ?? false,
        }
      else if (dataTypeId === CustomFieldDataType.List.id) {
        const items =
          customFieldIdToProjectCustomField
            .get(id)
            ?.customListItems?.map(({ value }) => ({ text: value, value })) ??
          []
        if (metadata?.styleId === CustomListItemStyle.Multiple.id)
          return {
            with: 'callback',
            items,
            onFilter: (value, { customFields }) =>
              find(
                customFields?.listValues?.multiples,
                id
              )?.customListItems?.some((item) => item.value === value) ?? false,
          }
        else if (metadata?.styleId === CustomListItemStyle.Single.id)
          return {
            with: 'callback',
            items,
            onFilter: (value, { customFields }) =>
              find(customFields?.listValues?.singles, id)?.customListItem
                ?.value === value,
          }
        else if (metadata?.styleId === CustomListItemStyle.Checkboxes.id)
          return {
            type: 'checkboxes',
            items:
              renderedAsCheckboxes?.checkboxes
                ?.find((cb) => cb.id === id)
                ?.customListItems?.map(({ id, value }) => ({
                  text: value,
                  value: id,
                })) ?? [],
            value: (value, { customFields }) =>
              find(customFields?.listValues?.checkboxes, id)?.booleans?.find(
                ({ customListItemId }) => value === customListItemId
              )?.checked,
          }
      } else if (dataTypeId === CustomFieldDataType.Boolean.id)
        return {
          type: 'boolean',
          with: 'value',
          value: ({ customFields }) =>
            find(customFields?.booleanValues, id)?.value,
        }
      else if (dataTypeId === CustomFieldDataType.Date.id)
        return {
          type: 'date',
          with: 'value',
          value: ({ customFields }) =>
            find(customFields?.dateValues, id)?.value,
        }
      return undefined
    },
    [customFieldIdToProjectCustomField, renderedAsCheckboxes?.checkboxes]
  )
  useEffect(() => {
    if (!records) return
    if (records.every((record) => !record?.customFields)) return
    /**
     * we can filter out deleted fields safely here because setting
     * `alwaysVisible` in cf2ct will unset prominent and disable it until you
     * restore the field
     */
    const prominent = customFields
      ?.filter(
        ({ customFieldToClassTypes, deleted }) =>
          !deleted &&
          customFieldToClassTypes.some(
            (cf2ct) => cf2ct.classTypeId === classType?.id && cf2ct.prominent
          )
      )
      .sort(alphabeticalSort('name'))
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    return setColumns(
      prominent.map((customField) => {
        const {
          id: customFieldId,
          name,
          description,
          dataTypeId,
          metadata,
        } = customField
        const sort = sortOptions({ customFieldId, dataTypeId })
        const filter = filterOptions(customField)
        return {
          width: width({ customField, filter: !!filter, sort: !!sort }),
          title: name,
          help: description,
          align: align(dataTypeId),
          sort,
          filter,
          render: (record) => {
            const disabledStates = disableEditWhen(record) ?? undefined
            const { customFields } = record
            if (dataTypeId === CustomFieldDataType.Text.id)
              return find(customFields?.textValues, customFieldId)?.value
            else if (dataTypeId === CustomFieldDataType.Boolean.id) {
              const value =
                find(customFields?.booleanValues, customFieldId)?.value ?? false
              const key = `${customFieldId}-${JSON.stringify(record)}`
              return (
                <CIMSCheckbox
                  checked={value}
                  loading={singleLoading(key)}
                  onChange={() => single(record, { customFieldId, value })}
                  disabled={disabledById({
                    customFieldId,
                    alreadyDisabled: disabledStates?.alreadyDisabled ?? false,
                    recordInactive: disabledStates?.recordInactive ?? false,
                  })}
                />
              )
            } else if (dataTypeId === CustomFieldDataType.Date.id) {
              const value = find(customFields?.dateValues, customFieldId)?.value
              if (metadata?.timestamp) return dateFormatter.shortDatetime(value)
              return dateFormatter.simple(value)
            } else if (dataTypeId === CustomFieldDataType.Entity.id) {
              return (
                <EntityCustomFields
                  column
                  entities={
                    find(customFields?.entityValues, customFieldId)?.entities ??
                    []
                  }
                  customFieldId={customFieldId}
                />
              )
            } else if (dataTypeId === CustomFieldDataType.List.id) {
              if (metadata?.styleId === CustomListItemStyle.Checkboxes.id) {
                const items =
                  (renderedAsCheckboxes?.checkboxes ?? []).find(
                    ({ id }) => id === customFieldId
                  )?.customListItems ?? []
                const { booleans } =
                  find(customFields?.listValues?.checkboxes, customFieldId) ??
                  {}
                return (
                  <CIMSChecklist
                    items={items
                      .reduce((items, { id, value }) => {
                        const checked =
                          booleans?.find(
                            ({ customListItemId }) => id === customListItemId
                          )?.checked ?? false
                        items.push({ checked, customListItemId: id, value })
                        return items
                      }, Array<{ checked: boolean; customListItemId: string; value: string }>())
                      .map(
                        ({ checked, customListItemId, value }, _, reduced) => {
                          const key = `${customFieldId}-${customListItemId}-${JSON.stringify(
                            record
                          )}`
                          return {
                            key,
                            disabled: disabledById({
                              customFieldId,
                              alreadyDisabled:
                                disabledStates?.alreadyDisabled ?? false,
                              recordInactive:
                                disabledStates?.recordInactive ?? false,
                            }),
                            tag: { children: value },
                            checked,
                            onChange: () =>
                              onCheckBooleans?.(record, {
                                customFieldId,
                                booleans: reduced.map(
                                  ({ customListItemId: id, checked }) => {
                                    if (id === customListItemId)
                                      checked = !checked
                                    return { customListItemId: id, checked }
                                  }
                                ),
                              }),
                          }
                        }
                      )}
                  />
                )
              } else if (
                metadata?.styleId === CustomListItemStyle.Multiple.id
              ) {
                const items =
                  find(customFields?.listValues?.multiples, customFieldId)
                    ?.customListItems ?? []
                return (
                  <CIMSTags
                    direction={{ tags: 'vertical' }}
                    tags={items.map(({ value }) => ({
                      text: value,
                      style: { verticalAlign: 'top' },
                    }))}
                  />
                )
              }
              const item = find(
                customFields?.listValues?.singles,
                customFieldId
              )?.customListItem?.value
              return item ? (
                <CIMSTag style={{ verticalAlign: 'top' }}>{item}</CIMSTag>
              ) : (
                ''
              )
            } else if (dataTypeId === CustomFieldDataType.Number.id)
              return find(
                customFields?.numberValues,
                customFieldId
              )?.value?.toLocaleString()
            else if (dataTypeId === CustomFieldDataType.Report.id)
              return (
                <ReportCustomFields
                  column
                  customFieldId={customFieldId}
                  reports={
                    find(customFields?.reportValues, customFieldId)?.reports ??
                    []
                  }
                />
              )
            return `Invalid dataTypeId: ${dataTypeId}`
          },
        }
      })
    )
  }, [
    classType,
    customFields,
    filterOptions,
    records,
    renderedAsCheckboxes?.checkboxes,
    single,
    singleLoading,
    disabledById,
    disableEditWhen,
    onCheckBooleans,
  ])

  return columns
}
